import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { CheckCircle2, MapPin, Briefcase } from "lucide-react";
import { get } from "@/lib/api";
import { Skel } from "@/components/app/Common";
import { Chips } from "@/components/app/ChipInput";
import { ApplyForm } from "@/pages/careers/ApplyForm";
import { CareersFrame as Frame, RoleNotFound, NotAccepting, SeeOpenRoles } from "@/pages/careers/CareersIndex";

const NEXT = [
  ["Today", "We've emailed you a confirmation with your reference number."],
  ["Within a few days", "Our team reviews your application against the role. A person reads every one."],
  ["If it's a match", "We'll invite you to the next step, such as a short questionnaire or interview."],
  ["Either way", "We'll let you know the outcome by email."],
];

function Confirmation({ result, color, supportEmail }) {
  return (
    <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.28 }} className="panel space-y-6 p-6 sm:p-8" data-testid="apply-confirmation">
      <div className="space-y-3">
        <CheckCircle2 size={36} style={{ color }} aria-hidden="true" />
        <h1 className="text-2xl font-semibold">Application received</h1>
        <p className="text-ink-secondary">Thanks for applying for <strong>{result.job_title}</strong> at {result.company_name}.</p>
        <p className="inline-flex rounded-[5px] border border-line bg-subtle px-3 py-2 font-mono text-sm" data-testid="apply-reference">Reference: {result.reference}</p>
        {result.portal_url && <p className="text-sm text-ink-secondary">Your private privacy link (also emailed): <a href={result.portal_url} className="font-medium underline" style={{ color }} data-testid="apply-portal-link">Manage my data</a>. Use it to see, download or correct your data, withdraw consent or ask for erasure.</p>}
      </div>
      <section className="space-y-3" data-testid="apply-next-timeline">
        <h2 className="text-base font-semibold">What happens next</h2>
        <ol className="relative space-y-4 border-l border-line pl-5">
          {NEXT.map(([t, d]) => (
            <li key={t} className="relative"><span className="absolute -left-[26px] top-1 h-3 w-3 rounded-full border-2 border-white" style={{ background: color }} />
              <p className="text-sm font-medium">{t}</p><p className="text-sm text-ink-secondary">{d}</p></li>
          ))}
        </ol>
      </section>
      <div className="flex flex-wrap items-center gap-3"><SeeOpenRoles testId="apply-see-other-roles" /><span className="text-sm text-ink-muted">Questions? Contact {supportEmail}.</span></div>
    </motion.div>
  );
}

export function CareersPage() {
  const { slug } = useParams();
  const [result, setResult] = useState(null);
  const { data: job, isLoading, error } = useQuery({ queryKey: ["careers", slug], queryFn: () => get(`/public/careers/${slug}`), retry: (n, e) => !e?.response && n < 2 });
  const { data: brand } = useQuery({ queryKey: ["public-branding"], queryFn: () => get("/public/branding"), enabled: !!error });
  const color = job?.primary_color || "#1F4D3A";

  if (isLoading) return <Frame><Skel className="h-64" /></Frame>;
  if (error) return <Frame brand={brand}>{error.response?.status === 410 ? <NotAccepting /> : <RoleNotFound />}</Frame>;
  if (result) return <Frame brand={job}><Confirmation result={result} color={color} supportEmail={job.support_email} /></Frame>;

  return (
    <Frame brand={job}>
      <article className="panel space-y-5 p-5 sm:p-8" data-testid="careers-page">
        <div className="space-y-2">
          <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl" data-tour="pt-careers-job-title" data-testid="careers-job-title">{job.title}</h1>
          <p className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-ink-muted">
            {job.location && <span className="inline-flex items-center gap-1"><MapPin size={14} /> {job.location}</span>}
            {[job.practice_area, job.law_firm, job.pqe].filter(Boolean).length > 0 && <span className="inline-flex items-center gap-1" data-testid="careers-job-meta"><Briefcase size={14} /> {[job.practice_area, job.law_firm, job.pqe].filter(Boolean).join(" · ")}</span>}
            {(job.department || job.employment_type) && <span className="inline-flex items-center gap-1"><Briefcase size={14} /> {[job.department, job.employment_type].filter(Boolean).join(" · ")}</span>}
          </p>
        </div>
        {job.intro && <p className="text-ink-secondary">{job.intro}</p>}
        {job.summary && <p className="leading-relaxed text-ink-secondary">{job.summary}</p>}
        {job.responsibilities.length > 0 && (
          <section><h2 className="mb-2 text-base font-semibold">What you'll do</h2>
            <ul className="list-disc space-y-1 pl-5 text-sm text-ink-secondary">{job.responsibilities.map((r) => <li key={r}>{r}</li>)}</ul></section>
        )}
        <section className="space-y-2"><h2 className="text-base font-semibold">What you'll need</h2><Chips values={job.must_have_skills} /></section>
        {job.nice_to_have_skills.length > 0 && <section className="space-y-2"><h2 className="text-base font-semibold">Nice to have</h2><Chips values={job.nice_to_have_skills} tone="neutral" /></section>}
      </article>
      <ApplyForm slug={slug} job={job} color={color} onDone={(r) => { setResult(r); window.scrollTo({ top: 0 }); }} />
    </Frame>
  );
}
